import { useContext, useState } from 'react'
import { useSelector } from 'react-redux'

import api from 'containers/api'
import { selectToken } from 'containers/authentication/authenticationSlice'
import OrganizationsContext from 'containers/organizations/organizationsContext'
import { IShopifyPromoCode } from 'types'

interface IShopifyPromoCodeProps {
  shopDomain: string | null
  annual: boolean
  setAnnual: (annual: boolean) => void
  input: string
  setInput: (value: string) => void
  applied: IShopifyPromoCode | null
  setApplied: (promo: IShopifyPromoCode | null) => void
}

const intervalOf = (annual: boolean) => (annual ? 'ANNUAL' : 'EVERY_30_DAYS')

// "Have a promo code?" on the Shopify plan page. Applying checks the code against the
// store and switches the billing toggle to the billing the code is for; the subscribe
// call checks it again, so whatever is typed here is never dropped silently.
const ShopifyPromoCode = ({ shopDomain, annual, setAnnual, input, setInput, applied, setApplied }: IShopifyPromoCodeProps) => {
  const { currentOrganization } = useContext(OrganizationsContext)
  const token = useSelector(selectToken)
  const [open, setOpen] = useState(false)
  const [checking, setChecking] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const apply = async (e?: React.FormEvent) => {
    e?.preventDefault()
    const code = input.trim()
    if (!code || checking) return
    setChecking(true)
    setError(null)
    try {
      const promo = await api.checkShopifyPromoCode({
        organizationId: currentOrganization.id,
        token,
        code,
        shop: shopDomain || undefined,
      })
      setApplied(promo)
      setInput(promo.code)
      setAnnual(promo.billing_interval === 'ANNUAL')
    } catch (err: any) {
      setApplied(null)
      const detail = err?.response?.data?.detail
      // A 400 carries the reason (invalid, expired, used up, wrong billing...). Anything
      // else is our failure, not the code's: say so rather than call the code invalid.
      setError(
        err?.response?.status === 400 && detail?.message
          ? detail.message
          : 'We couldn’t check this code right now. Try again in a moment.'
      )
    } finally {
      setChecking(false)
    }
  }

  const remove = () => {
    setApplied(null)
    setInput('')
    setError(null)
  }

  if (applied) {
    const otherBilling = applied.billing_interval !== intervalOf(annual)
    return (
      <div className="promo-code">
        <div className={`promo-code-applied ${otherBilling ? 'warning' : ''}`}>
          <span>
            {otherBilling
              ? `${applied.code} is for ${applied.billing_interval === 'ANNUAL' ? 'annual' : 'monthly'} billing. Switch to ${applied.billing_interval === 'ANNUAL' ? 'Annual' : 'Monthly'} Billing to use it.`
              : `${applied.code} applied: ${applied.description}`}
          </span>
          <button type="button" className="promo-code-link" onClick={remove}>
            Remove
          </button>
        </div>
      </div>
    )
  }

  if (!open) {
    return (
      <div className="promo-code">
        <button type="button" className="promo-code-link" onClick={() => setOpen(true)}>
          Have a promo code?
        </button>
      </div>
    )
  }

  return (
    <form className="promo-code" onSubmit={apply}>
      <div className="promo-code-row">
        <input
          type="text"
          value={input}
          onChange={e => {
            setInput(e.target.value)
            setError(null)
          }}
          placeholder="Promo code"
          aria-label="Promo code"
          maxLength={50}
          autoComplete="off"
          autoFocus
        />
        <button type="submit" disabled={!input.trim() || checking}>
          {checking ? 'Checking…' : 'Apply'}
        </button>
      </div>
      {error && <p className="promo-code-error">{error}</p>}
    </form>
  )
}

export default ShopifyPromoCode
